import * as React from "react";
import { Store } from "redux";
import ReactTable, { type ReactTableColumn } from "@/modules/shared/ui/ReactTable";
import { connect } from "react-redux";
import { createReduxStore } from "@/store/reduxHelpers";
import { translate } from "@/modules/shared/IntlProviderWrapper";
import TicketStatus from "./TicketStatus";
import { ActionCell } from "./ActionCell";
import { SET_INBOX_ITEM_VIEW } from "./reducers/ticketing";
import TicketDetailsModal from "./TicketDetailsModal";
import { getBackend as getBackend2 } from "@/lib/backend/backend2";
import * as t from "@/lib/backend/ticketing.generated.types";
import { niceDateTime } from "@/lib/util";
import local_settings, { FIELD_LAST_LINEGROUP } from "@/lib/localSettings";

import { reducers } from "./reducers/reducers";
import type { SelectInboxItemView } from "./reducers/ticketing";
import type { ReduxState } from "./reducers/reducers";
import type { Cell } from "react-table";

type OwnProps = {};
type Props = OwnProps & ReduxProps;

type State = {
    show_ticket_details_uuid: string | null;
    show_ticket_edit_uuid: string | null;
    loading: boolean;
};

export type CellArgs = {
    cell: Cell<t.ITicketShortInfoExt>;
};

const StatusCell = ({ cell }: CellArgs): React.ReactNode => {
    return (
        <TicketStatus
            status={cell.value}
            is_closed={cell.row.original.is_closed}
            is_error={cell.row.original.is_error}
            color={cell.row.original.status_color}
            bgcolor={cell.row.original.status_bgcolor}
        />
    );
};

const DateCell = ({ cell }: CellArgs): React.ReactNode => {
    return <span>{niceDateTime(new Date(cell.value))}</span>;
};

const MaterialTitleIdCell = ({ cell }: CellArgs): React.ReactNode => {
    const { original } = cell.row;
    let cell_data = "N/A";
    if (original.material_id) {
        cell_data = original.material_id;
    }
    if (original.material_title) {
        cell_data += " - " + original.material_title;
    }

    return <span>{cell_data}</span>;
};

const getColumns = (
    onClickDetails: (ticket_uuid: string) => void,
    onClickEdit: (ticket_uuid: string) => void
): ReactTableColumn<Partial<t.ITicketShortInfoExt>>[] => {
    return [
        {
            Header: "",
            filterInputPlaceholder: translate("common.order", "Order"),
            accessor: "order_id",
        },
        {
            Header: "",
            filterInputPlaceholder: translate("Ticketing.material_id_title", "Material ID - Title"),
            accessor: "material_id",
            Cell: MaterialTitleIdCell,
        },
        {
            Header: "",
            filterInputPlaceholder: translate("common.line", "Line"),
            accessor: "line_title",
        },
        {
            Header: "",
            filterInputPlaceholder: translate("Ticketing.assignee", "Assignee"),
            accessor: "assigned_to",
        },
        {
            Header: translate("Ticketing.assigned_on", "Assigned on"),
            accessor: "assigned_on",
            disableFilters: true,
            Cell: DateCell,
            enableSorting: true,
        },
        {
            Header: translate("common.version", "Version"),
            accessor: "workflow_version",
            disableFilters: true,
        },
        {
            Header: translate("common.status", "Status"),
            accessor: "status_title",
            disableFilters: true,
            Cell: StatusCell,
            enableSorting: true,
        },
        {
            Header: "",
            accessor: "uuid",
            disableFilters: true,
            Cell: ActionCell(onClickDetails, onClickEdit),
        },
    ];
};

class TicketList extends React.Component<Props, State> {
    store: Store<ReduxState>;

    constructor(props: Props) {
        super(props);
        this.store = createReduxStore(reducers);
    }

    state: State = {
        show_ticket_details_uuid: null,
        show_ticket_edit_uuid: null,
        loading: true,
    };

    componentDidMount() {
        this.load();
    }
    componentDidUpdate(prev_props: Props) {
        if (prev_props.selected_workflow != this.props.selected_workflow) {
            this.load();
        }
    }

    async load() {
        const selected_workflow = this.props.selected_workflow;
        if (selected_workflow == null) {
            this.setState({ loading: false });
            return;
        }
        const res = await getBackend2().ticketing.searchTickets({
            include_closed: this.props.include_closed,
            include_errored: true,
            workflow_name: selected_workflow,
            linegroup_uuid: local_settings.getItem(FIELD_LAST_LINEGROUP) || "",
        });
        const tickets = res.hits.sort((a, b) => a.assigned_on - b.assigned_on);

        if (this.store !== null) {
            this.store.getState().ticketing.tickets = tickets;
        }
        this.setState({ loading: false });
    }

    onClickDetails = (ticket_uuid: string) => {
        this.setState({ show_ticket_details_uuid: ticket_uuid });
    };

    onCloseDetails = () => {
        this.setState({ show_ticket_details_uuid: null });
    };

    onClickEdit = (ticket_uuid: string) => {
        this.props.reduxDispatch({ type: SET_INBOX_ITEM_VIEW, data: ticket_uuid });
    };

    onCellClick = (cell: Cell<t.ITicketShortInfoExt>) => {
        const uuid = cell.row.values.uuid;
        this.setState({ show_ticket_details_uuid: uuid });
    };

    onRequestRefresh = async () => {
        await this.load();
    };

    render() {
        return (
            <div className="main-body">
                <ReactTable
                    isLoading={this.state.loading}
                    small_loader={true}
                    columns={getColumns(this.onClickDetails, this.onClickEdit)}
                    data={this.props.tickets || []}
                    onCellClick={this.onCellClick}
                    hiddenColumns={["workflow_name", "workflow_version"]}
                />
                <TicketDetailsModal
                    show_ticket_details_uuid={this.state.show_ticket_details_uuid}
                    onClose={this.onCloseDetails}
                    requestRefreshCallback={this.onRequestRefresh}
                />
            </div>
        );
    }
}

const mapState = (state: ReduxState) => {
    const selected_workflow = state.ticketing.selected_workflow;
    const tickets = state.ticketing.tickets;
    const include_closed = state.ticketing.include_closed;

    return { include_closed, selected_workflow, tickets };
};

const mapDispatch = (dispatch: (action: SelectInboxItemView) => void) => ({ reduxDispatch: dispatch });

type ReduxProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;
export default connect<ReturnType<typeof mapState>, ReturnType<typeof mapDispatch>, OwnProps, ReduxState>(
    mapState,
    mapDispatch
)(TicketList);
